import React, { useMemo } from 'react';
import Select, { components } from 'react-select';
import shortUUID from 'short-uuid';
import { Tooltip } from '../Tooltip';
import { ValidateError } from '../ErrorMessage';

export const ComboBox = ({
  id,
  label,
  labelClass,
  labelIcon,
  color,
  className,
  tabIndex,
  options,
  defaultValue,
  value,
  large,
  small,
  disabled,
  onChange,
  field,
  form,
  tooltip,
  ...props
}) => {
  const uuid = useMemo(() => shortUUID().generate(), []);
  const borderColor = form
    ? form.errors[field.name] && form.touched[field.name]
      ? '#bd0000'
      : ''
    : color === 'orange'
    ? '#e87400'
    : false;
  const customStyles = {
    control: (provided, { isDisabled, menuIsOpen }) => ({
      ...provided,
      boxShadow: 0,
      borderRadius: menuIsOpen
        ? '25px 25px 0 0'
        : large
        ? menuIsOpen
          ? '50px 50px 0 0'
          : 50
        : 25,
      padding: '8px 24px',
      backgroundColor:
        color === 'orange' ? 'transparent' : isDisabled ? '#e0e0e0' : '#fff',
      borderWidth: 1,
      borderStyle: 'solid',
      borderColor: borderColor || (isDisabled ? '#e0e0e0' : '#727272'),
      '&:hover': {
        borderColor: color === 'orange' ? '#d07000' : '#383a35',
      },
      fontSize: large ? 16 : 14,
      height: large ? 56 : 38,
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: 0,
    }),
    input: (provided) => ({
      ...provided,
      padding: 0,
      margin: 0,
    }),
    singleValue: (provided, { isDisabled }) => ({
      ...provided,
      color: color === 'orange' ? '#e87400' : isDisabled && '#727272',
      fontWeight: color === 'orange' ? 'bold' : '400',
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      display: 'none',
    }),
    dropdownIndicator: (provided, { isDisabled }) => ({
      ...provided,
      padding: 0,
      color:
        color === 'orange' ? '#e87400' : isDisabled ? '#EDEDED' : '#727272',
      '&:hover': {
        color: color === 'orange' ? '#d07000' : '#383a35',
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#727272',
    }),
    menu: (provided, state) => ({
      ...provided,
      color: state.selectProps.menuColor,
      backgroundColor: '#ffffff',
      borderRadius: '0 0 24px 24px',
      whiteSpace: 'nowrap',
      margin: 0,
      fontSize: 14,
      boxShadow: 'none',
      borderWidth: '0px 1px 1px 1px',
      borderStyle: 'solid',
      borderColor: color === 'orange' ? '#e87400' : '#727272',
      paddingBottom: options.length > 7 ? 24 : 0,
    }),
    menuList: (provided) => ({
      ...provided,
      padding: 0,
    }),
    option: (provided, state) => ({
      ...provided,
      padding: '10px 24px',
      backgroundColor: state.isSelected ? '#E0E0E0' : '#fff',
      color: state.isSelected ? '#383a35' : '#727272',
      borderTop: '1px solid #E0E0E0',
      '&:hover': {
        backgroundColor: state.isSelected ? '#E0E0E0' : '#f4f4f2',
      },
      '&:first-of-type': {
        borderTop: 0,
      },
      '&:last-child': {
        borderRadius: '0 0 24px 24px',
      },
    }),
  };

  const NoOptionsMessage = React.memo(function CustomNoOptionsMessage(
    noOptMessageProps
  ) {
    return (
      <components.NoOptionsMessage {...noOptMessageProps}>
        <span className="custom-css-class">Nenhum item encontrado</span>
      </components.NoOptionsMessage>
    );
  });
  return (
    <div className={className}>
      {label ? (
        <label
          className={`label ${labelClass} ${disabled && 'disabled'} ${
            form &&
            form.errors[field.name] &&
            form.touched[field.name] &&
            'error'
          }`}
          htmlFor={id || uuid}
        >
          {label}
          {tooltip && (
            <Tooltip title={tooltip} arrow placement="right">
              ?
            </Tooltip>
          )}
          {labelIcon ? <i className={`ms-2 ${labelIcon}`} /> : ''}
        </label>
      ) : (
        ''
      )}
      {field ? (
        <div className="select-wrapper">
          <div>
            <Select
              {...props}
              {...field}
              styles={customStyles}
              components={{ NoOptionsMessage }}
              isDisabled={disabled}
              name={field.name}
              classNamePrefix="select"
              placeholder="Selecione"
              inputId={id || uuid}
              value={
                field.value
                  ? options.find((option) => option.value === field.value)
                  : ''
              }
              onChange={(option) => {
                form.setFieldValue(field.name, option.value);

                if (onChange) {
                  onChange(option);
                }
              }}
              onBlur={() => form.setFieldTouched(field.name)}
              options={options}
            />
            {form.touched[field.name] && form.errors[field.name] && (
              <ValidateError>{form.errors[field.name]}</ValidateError>
            )}
          </div>
        </div>
      ) : (
        <div>
          <Select
            {...props}
            disabled={disabled}
            styles={customStyles}
            components={{ NoOptionsMessage }}
            placeholder="Selecione"
            defaultValue={
              options
                ? options.find((option) => option.value === defaultValue)
                  ? defaultValue
                  : ''
                : ''
            }
            value={options.find((option) => option.value === value)}
            onChange={(option) => onChange(option.value)}
            options={options}
          />
        </div>
      )}
    </div>
  );
};
